import styled from '@emotion/styled';
import { media } from '@planit.family/theme';

// ─── Page ─────────────────────────────────────────────────────────────────────

export const StyledLandingPage = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.colors.background.primary};
  color: ${(p) => p.theme.colors.text.primary};
  font-family: ${(p) => p.theme.font.family.base};
`;

// ─── Nav ──────────────────────────────────────────────────────────────────────

export const StyledNav = styled.header`
  position: sticky;
  top: 0;
  z-index: ${(p) => p.theme.zIndex.sticky};
  background: ${(p) => p.theme.colors.background.primary};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral[200]};
`;

export const StyledNavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${(p) => p.theme.space[4]} ${(p) => p.theme.space[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StyledNavLogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space[2]};
  cursor: pointer;
  user-select: none;
`;

export const StyledNavLinks = styled.nav`
  display: none;
  align-items: center;
  gap: ${(p) => p.theme.space[8]};

  ${media.md} {
    display: flex;
  }
`;

export const StyledNavLink = styled.a`
  font-size: ${(p) => p.theme.font.size.bodySm};
  font-weight: ${(p) => p.theme.font.weight.medium};
  color: ${(p) => p.theme.colors.text.secondary};
  text-decoration: none;
  cursor: pointer;
  transition: color ${(p) => p.theme.transition.fast};

  &:hover {
    color: ${(p) => p.theme.colors.text.primary};
  }
`;

export const StyledNavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space[3]};
`;

export const StyledNavLoginBtn = styled.button`
  font-size: ${(p) => p.theme.font.size.bodySm};
  font-weight: ${(p) => p.theme.font.weight.medium};
  color: ${(p) => p.theme.colors.text.primary};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.neutral[300]};
  border-radius: ${(p) => p.theme.radius.full};
  padding: ${(p) => p.theme.space[2]} ${(p) => p.theme.space[4]};
  cursor: pointer;
  transition: all ${(p) => p.theme.transition.fast};

  &:hover {
    border-color: ${(p) => p.theme.colors.primary.gold};
    color: ${(p) => p.theme.colors.primary.gold};
  }
`;

export const StyledNavCTABtn = styled.button`
  font-size: ${(p) => p.theme.font.size.bodySm};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: #1c2024;
  background: ${(p) => p.theme.colors.primary.gold};
  border: none;
  border-radius: ${(p) => p.theme.radius.full};
  padding: ${(p) => p.theme.space[2]} ${(p) => p.theme.space[4]};
  cursor: pointer;
  transition: all ${(p) => p.theme.transition.fast};

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────

export const StyledHeroSection = styled.section`
  padding: ${(p) => p.theme.space[16]} ${(p) => p.theme.space[6]} ${(p) => p.theme.space[12]};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 80% 50% at 50% -10%,
      ${(p) => p.theme.colors.primary.gold}20 0%,
      transparent 65%
    );
    pointer-events: none;
  }

  ${media.md} {
    padding: ${(p) => p.theme.space[24]} ${(p) => p.theme.space[8]} ${(p) => p.theme.space[16]};
  }
`;

export const StyledHeroInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const StyledHeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.space[2]};
  padding: ${(p) => p.theme.space[1]} ${(p) => p.theme.space[4]};
  background: ${(p) => p.theme.colors.primary.gold}1a;
  border: 1px solid ${(p) => p.theme.colors.primary.gold}40;
  border-radius: ${(p) => p.theme.radius.full};
  font-size: ${(p) => p.theme.font.size.caption};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.primary.gold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: ${(p) => p.theme.space[6]};
`;

export const StyledHeroTitle = styled.h1`
  font-family: ${(p) => p.theme.font.family.heading};
  font-size: 2.6rem;
  font-weight: ${(p) => p.theme.font.weight.bold};
  line-height: ${(p) => p.theme.font.lineHeight.tight};
  color: ${(p) => p.theme.colors.text.primary};
  margin: 0 0 ${(p) => p.theme.space[6]};
  letter-spacing: -0.02em;

  ${media.md} {
    font-size: 3.75rem;
  }

  ${media.lg} {
    font-size: 4.5rem;
  }
`;

export const StyledHeroTitleAccent = styled.em`
  color: ${(p) => p.theme.colors.primary.gold};
  font-style: italic;
`;

export const StyledHeroSubtitle = styled.p`
  font-size: ${(p) => p.theme.font.size.bodyLg};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: ${(p) => p.theme.font.lineHeight.relaxed};
  max-width: 560px;
  margin: 0 auto ${(p) => p.theme.space[8]};
`;

export const StyledHeroCTARow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => p.theme.space[3]};

  ${media.sm} {
    flex-direction: row;
    justify-content: center;
  }
`;

export const StyledHeroPrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(p) => p.theme.space[3]} ${(p) => p.theme.space[8]};
  background: ${(p) => p.theme.colors.primary.gold};
  color: #1c2024;
  font-size: ${(p) => p.theme.font.size.body};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  border: none;
  border-radius: ${(p) => p.theme.radius.full};
  cursor: pointer;
  transition: all ${(p) => p.theme.transition.fast};
  white-space: nowrap;

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: ${(p) => p.theme.shadow.lg};
  }
`;

// ─── App Mockup ───────────────────────────────────────────────────────────────

export const StyledMockupWrapper = styled.div`
  margin: ${(p) => p.theme.space[12]} auto 0;
  max-width: 880px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 10%;
    right: 10%;
    height: 60px;
    background: ${(p) => p.theme.colors.primary.gold}20;
    filter: blur(40px);
    border-radius: 50%;
  }
`;

export const StyledMockupFrame = styled.div`
  background: ${(p) => p.theme.colors.background.secondary};
  border-radius: ${(p) => p.theme.radius['2xl']};
  border: 1px solid ${(p) => p.theme.colors.neutral[200]};
  box-shadow: ${(p) => p.theme.shadow['2xl']};
  overflow: hidden;
`;

export const StyledMockupBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space[2]};
  padding: ${(p) => p.theme.space[3]} ${(p) => p.theme.space[4]};
  background: ${(p) => p.theme.colors.background.tertiary};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral[200]};
`;

export const StyledMockupDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const StyledMockupUrlBar = styled.div`
  flex: 1;
  background: ${(p) => p.theme.colors.neutral[200]};
  border-radius: ${(p) => p.theme.radius.sm};
  height: 20px;
  max-width: 280px;
  margin: 0 auto;
`;

export const StyledMockupBody = styled.div`
  display: flex;
  height: 300px;

  ${media.md} {
    height: 380px;
  }
`;

export const StyledMockupSidebar = styled.div`
  width: 160px;
  flex-shrink: 0;
  background: ${(p) => p.theme.colors.background.primary};
  border-right: 1px solid ${(p) => p.theme.colors.neutral[200]};
  padding: ${(p) => p.theme.space[4]};
  display: none;
  flex-direction: column;
  gap: ${(p) => p.theme.space[2]};

  ${media.md} {
    display: flex;
  }
`;

export const StyledMockupSidebarItem = styled.div`
  height: 28px;
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.colors.neutral[200]};
  opacity: 0.5;
`;

export const StyledMockupSidebarItemActive = styled.div`
  height: 28px;
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.colors.primary.gold}33;
  border: 1px solid ${(p) => p.theme.colors.primary.gold}44;
`;

export const StyledMockupMain = styled.div`
  flex: 1;
  padding: ${(p) => p.theme.space[4]} ${(p) => p.theme.space[6]};
  background: ${(p) => p.theme.colors.background.secondary};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space[3]};
`;

export const StyledMockupHeaderBar = styled.div`
  height: 20px;
  background: ${(p) => p.theme.colors.neutral[200]};
  border-radius: ${(p) => p.theme.radius.sm};
  width: 40%;
  opacity: 0.6;
`;

export const StyledMockupListRow = styled.div`
  height: 16px;
  background: ${(p) => p.theme.colors.neutral[200]};
  border-radius: ${(p) => p.theme.radius.sm};
  opacity: 0.3;
`;

export const StyledMockupCard = styled.div`
  background: ${(p) => p.theme.colors.background.primary};
  border-radius: ${(p) => p.theme.radius.xl};
  overflow: hidden;
  box-shadow: ${(p) => p.theme.shadow.md};
  margin-top: auto;
  max-width: 340px;
`;

export const StyledMockupCardImage = styled.div`
  height: 140px;
  background: linear-gradient(
    to bottom right,
    #f2b49a 0%,
    #d4785a 35%,
    #c05a3c 65%,
    #a84828 100%
  );
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: ${(p) => p.theme.space[3]};

  ${media.md} {
    height: 170px;
  }
`;

export const StyledMockupCardLabel = styled.div`
  background: rgba(255, 255, 255, 0.94);
  color: #1c2024;
  padding: ${(p) => p.theme.space[1]} ${(p) => p.theme.space[3]};
  border-radius: ${(p) => p.theme.radius.md};
  font-size: 0.7rem;
  font-weight: ${(p) => p.theme.font.weight.semibold};
`;

// ─── Features ─────────────────────────────────────────────────────────────────

export const StyledFeaturesSection = styled.section`
  padding: ${(p) => p.theme.space[16]} ${(p) => p.theme.space[6]};

  ${media.md} {
    padding: ${(p) => p.theme.space[24]} ${(p) => p.theme.space[8]};
  }
`;

export const StyledFeaturesInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

export const StyledFeaturesLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(p) => p.theme.space[2]};
  font-size: ${(p) => p.theme.font.size.caption};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.primary.gold};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: ${(p) => p.theme.space[4]};
`;

export const StyledFeaturesTitle = styled.h2`
  font-family: ${(p) => p.theme.font.family.heading};
  font-size: ${(p) => p.theme.font.size.h1};
  font-weight: ${(p) => p.theme.font.weight.bold};
  color: ${(p) => p.theme.colors.text.primary};
  text-align: center;
  margin: 0 0 ${(p) => p.theme.space[2]};
  letter-spacing: -0.01em;

  ${media.md} {
    font-size: ${(p) => p.theme.font.size.display};
  }
`;

export const StyledFeaturesUnderline = styled.div`
  width: 56px;
  height: 3px;
  background: ${(p) => p.theme.colors.primary.gold};
  border-radius: 2px;
  margin: 0 auto ${(p) => p.theme.space[12]};
`;

export const StyledFeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(p) => p.theme.space[6]};

  ${media.md} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${(p) => p.theme.space[6]};
  }
`;

export const StyledFeatureCard = styled.div`
  background: ${(p) => p.theme.colors.background.secondary};
  border: 1px solid ${(p) => p.theme.colors.neutral[200]};
  border-radius: ${(p) => p.theme.radius['2xl']};
  padding: ${(p) => p.theme.space[8]};
  transition: transform ${(p) => p.theme.transition.normal},
    box-shadow ${(p) => p.theme.transition.normal};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${(p) => p.theme.shadow.xl};
  }
`;

export const StyledFeatureCardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${(p) => p.theme.radius.lg};
  background: ${(p) => p.theme.colors.primary.gold}1a;
  border: 1px solid ${(p) => p.theme.colors.primary.gold}33;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.colors.primary.gold};
  margin-bottom: ${(p) => p.theme.space[4]};
  flex-shrink: 0;
`;

export const StyledFeatureCardTitle = styled.h3`
  font-family: ${(p) => p.theme.font.family.heading};
  font-size: ${(p) => p.theme.font.size.h4};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.text.primary};
  margin: 0 0 ${(p) => p.theme.space[3]};
`;

export const StyledFeatureCardDesc = styled.p`
  font-size: ${(p) => p.theme.font.size.body};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: ${(p) => p.theme.font.lineHeight.relaxed};
  margin: 0;
`;

// ─── Testimonial ──────────────────────────────────────────────────────────────

export const StyledTestimonialSection = styled.section`
  padding: ${(p) => p.theme.space[16]} ${(p) => p.theme.space[6]};
  background: ${(p) => p.theme.colors.background.secondary};
  text-align: center;

  ${media.md} {
    padding: ${(p) => p.theme.space[24]} ${(p) => p.theme.space[8]};
  }
`;

export const StyledTestimonialInner = styled.div`
  max-width: 680px;
  margin: 0 auto;
`;

export const StyledQuoteMark = styled.div`
  font-family: Georgia, serif;
  font-size: 5rem;
  line-height: 1;
  color: ${(p) => p.theme.colors.primary.gold};
  margin-bottom: -1.5rem;
  user-select: none;
`;

export const StyledQuoteText = styled.blockquote`
  font-family: ${(p) => p.theme.font.family.heading};
  font-size: ${(p) => p.theme.font.size.h3};
  font-style: italic;
  color: ${(p) => p.theme.colors.text.primary};
  line-height: ${(p) => p.theme.font.lineHeight.relaxed};
  margin: 0;

  ${media.md} {
    font-size: ${(p) => p.theme.font.size.h2};
  }
`;

export const StyledQuoteAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(p) => p.theme.space[3]};
`;

export const StyledQuoteAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${(p) => p.theme.colors.primary.gold} 0%,
    ${(p) => p.theme.colors.accent.teal} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: ${(p) => p.theme.font.weight.semibold};
  font-size: 0.875rem;
  flex-shrink: 0;
`;

export const StyledQuoteName = styled.p`
  font-size: ${(p) => p.theme.font.size.body};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.text.primary};
  margin: 0;
`;

export const StyledQuoteRole = styled.p`
  font-size: ${(p) => p.theme.font.size.bodySm};
  color: ${(p) => p.theme.colors.text.secondary};
  margin: 0;
`;

// ─── Join CTA ─────────────────────────────────────────────────────────────────

export const StyledJoinSection = styled.section`
  padding: ${(p) => p.theme.space[16]} ${(p) => p.theme.space[6]};
  text-align: center;

  ${media.md} {
    padding: ${(p) => p.theme.space[24]} ${(p) => p.theme.space[8]};
  }
`;

export const StyledJoinInner = styled.div`
  max-width: 540px;
  margin: 0 auto;
`;

export const StyledJoinTitle = styled.h2`
  font-family: ${(p) => p.theme.font.family.heading};
  font-size: ${(p) => p.theme.font.size.h1};
  font-weight: ${(p) => p.theme.font.weight.bold};
  color: ${(p) => p.theme.colors.text.primary};
  margin: 0 0 ${(p) => p.theme.space[4]};
  letter-spacing: -0.01em;

  ${media.md} {
    font-size: ${(p) => p.theme.font.size.display};
  }
`;

export const StyledJoinSubtitle = styled.p`
  font-size: ${(p) => p.theme.font.size.body};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: ${(p) => p.theme.font.lineHeight.relaxed};
  margin: 0 0 ${(p) => p.theme.space[8]};
`;

export const StyledJoinButton = styled.button`
  padding: ${(p) => p.theme.space[3]} ${(p) => p.theme.space[6]};
  background: ${(p) => p.theme.colors.primary.gold};
  color: #1c2024;
  font-size: ${(p) => p.theme.font.size.body};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  border: none;
  border-radius: ${(p) => p.theme.radius.full};
  cursor: pointer;
  white-space: nowrap;
  transition: all ${(p) => p.theme.transition.fast};

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

export const StyledFooter = styled.footer`
  padding: ${(p) => p.theme.space[12]} ${(p) => p.theme.space[6]};
  background: ${(p) => p.theme.colors.background.secondary};
  border-top: 1px solid ${(p) => p.theme.colors.neutral[200]};

  ${media.md} {
    padding: ${(p) => p.theme.space[16]} ${(p) => p.theme.space[8]};
  }
`;

export const StyledFooterInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

export const StyledFooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(p) => p.theme.space[8]};
  padding-bottom: ${(p) => p.theme.space[8]};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral[200]};

  ${media.md} {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: ${(p) => p.theme.space[12]};
  }
`;

export const StyledFooterLogoArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space[3]};
  grid-column: 1 / -1;

  ${media.md} {
    grid-column: auto;
  }
`;

export const StyledFooterTagline = styled.p`
  font-size: ${(p) => p.theme.font.size.bodySm};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: ${(p) => p.theme.font.lineHeight.relaxed};
  max-width: 240px;
  margin: 0;
`;

export const StyledFooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space[3]};
`;

export const StyledFooterColumnTitle = styled.h4`
  font-size: ${(p) => p.theme.font.size.bodySm};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.text.primary};
  margin: 0 0 ${(p) => p.theme.space[1]};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const StyledFooterLink = styled.a`
  font-size: ${(p) => p.theme.font.size.bodySm};
  color: ${(p) => p.theme.colors.text.secondary};
  text-decoration: none;
  cursor: pointer;
  transition: color ${(p) => p.theme.transition.fast};

  &:hover {
    color: ${(p) => p.theme.colors.primary.gold};
  }
`;

export const StyledFooterBottom = styled.div`
  padding-top: ${(p) => p.theme.space[6]};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space[3]};

  ${media.md} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const StyledFooterCopyright = styled.p`
  font-size: ${(p) => p.theme.font.size.caption};
  color: ${(p) => p.theme.colors.text.disabled};
  margin: 0;
`;
